/**
 * "WHO CAN HELP" zone (§1.6 Build 4b-2, D-123.14). Class D (matched Professional Contacts)
 * and Class C (agency packs) as compact bordered cards, side by side + wrapping. The whole
 * zone hides when the task has no C/D bindings.
 */
import { Phone, Mail, Globe } from "lucide-react";
import type { ResolvedBinding, ResolvedRecordRow } from "@shared/task-bindings";
import { PROFESSIONAL_HELP_SHORT, type ProfessionalHelpRole } from "@shared/constants/professional-help";
import { SAGE, WARM } from "@shared/brand";


const LABEL_STYLE = { color: SAGE[600], fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" } as const;
const cardStyle = { backgroundColor: WARM.white, border: `1px solid ${WARM.stoneLight}`, borderRadius: 8 } as const;

function cell(row: ResolvedRecordRow, key: string): string | null {
  const c = row.cells.find((x) => x.fieldKey === key);
  return c && !c.secureFilePointer ? c.value : null;
}

export function WhoCanHelp({ bindings, professionalHelp = [] }: { bindings: ResolvedBinding[]; professionalHelp?: string[] }) {
  const dContacts = bindings.filter((b): b is Extract<ResolvedBinding, { class: "D" }> => b.class === "D").flatMap((b) => b.contacts);
  // s172: carry the binding's optional per-task `urlLabel` alongside the agency, so the link can
  // say what it is FOR on this task. The URL still comes from the registry — one destination, one
  // source; only the wording is task-scoped.
  const cAgencies = bindings
    .filter((b): b is Extract<ResolvedBinding, { class: "C" }> => b.class === "C")
    .map((b) => (b.agency ? { ...b.agency, urlLabel: b.urlLabel } : null))
    .filter((a): a is NonNullable<typeof a> => a != null);

  // s126 item 8: the recommended-professionals line coexists with the matched-contact cards
  // (the pill = the generic recommendation; a card = the actual person the planner recorded).
  const helpNames = professionalHelp
    .map((r) => PROFESSIONAL_HELP_SHORT[r as ProfessionalHelpRole])
    .filter(Boolean);

  if (dContacts.length === 0 && cAgencies.length === 0 && helpNames.length === 0) return null;

  return (
    <div className="mt-4" data-testid="who-can-help-zone">
      <div className="uppercase mb-2" style={LABEL_STYLE}>Who can help</div>
      {helpNames.length > 0 && (
        <div className="text-[13px] mb-2" style={{ color: WARM.taupe }} data-testid="who-can-help-professional">
          A professional is recommended for this task: {helpNames.join(" · ")}.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {dContacts.map((row) => {
          const name = cell(row, "name");
          const role = cell(row, "role");
          const phone = cell(row, "phone");
          const email = cell(row, "email");
          return (
            <div key={row.rowId} className="p-3 min-w-[200px]" style={cardStyle} data-testid={`help-contact-${row.rowId}`}>
              <div className="text-[13px]" style={{ color: WARM.charcoal, fontWeight: 500 }}>
                {name ?? "Contact"}{role ? ` — ${role}` : ""}
              </div>
              {(phone || email) && (
                <div className="text-[12px] mt-1 flex flex-col gap-0.5" style={{ color: WARM.taupe }}>
                  {phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {phone}</span>}
                  {email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {email}</span>}
                </div>
              )}
            </div>
          );
        })}
        {cAgencies.map((a) => (
          <div key={a.agencyKey} className="p-3 min-w-[200px] max-w-[320px]" style={cardStyle} data-testid={`help-agency-${a.agencyKey}`}>
            <div className="text-[13px]" style={{ color: WARM.charcoal, fontWeight: 500 }}>{a.name}</div>
            <div className="text-[12px] mt-1 flex flex-col gap-0.5" style={{ color: WARM.taupe }}>
              {a.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</span>}
              {a.url && <a href={a.url} target="_blank" rel="noopener" className="inline-flex items-center gap-1" style={{ color: SAGE[600] }} data-testid={`help-agency-link-${a.agencyKey}`}><Globe className="h-3 w-3" /> {a.urlLabel ?? "Website"}</a>}
              {a.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {a.email}</span>}
            </div>
            {a.requiredDocuments.length > 0 && (
              <div className="text-[12px] mt-1.5" style={{ color: WARM.taupe }} data-testid={`help-agency-docs-${a.agencyKey}`}>
                Bring: {a.requiredDocuments.join("; ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
